import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import logoImg from "@/assets/logo.png";

import {
  getResidentProperties,
  getStoredResidentMemberships,
  getStoredResidentSelectionToken,
  persistResidentAuthenticatedState,
  selectResidentProperty,
  switchResidentProperty,
  type ResidentMembership,
} from "@/api/residentAuth";
import { queryClient } from "@/lib/queryClient";

const PRIMARY = "#1F3A93";
const BG = "#F5F6FA";

export default function ResidentPropertySelectionPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [memberships, setMemberships] = React.useState<ResidentMembership[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [submittingId, setSubmittingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const residentToken = localStorage.getItem("resident_access_token");
  const selectionToken = getStoredResidentSelectionToken();

  React.useEffect(() => {
    let cancelled = false;

    async function loadMemberships() {
      try {
        setError(null);
        if (residentToken) {
          const response = await getResidentProperties();
          if (!cancelled) {
            setMemberships(response.memberships || []);
          }
        } else {
          const stored = getStoredResidentMemberships();
          if (!stored.length || !selectionToken) {
            navigate("/login", { replace: true });
            return;
          }
          if (!cancelled) {
            setMemberships(stored);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || t("resident.selectProperty.loadError"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadMemberships();
    return () => {
      cancelled = true;
    };
  }, [navigate, residentToken, selectionToken, t]);

  async function handleSelect(residentId: string) {
    setSubmittingId(residentId);
    setError(null);
    try {
      const response = residentToken
        ? await switchResidentProperty({ resident_id: residentId })
        : await selectResidentProperty({
            selection_token: selectionToken || "",
            resident_id: residentId,
          });

      queryClient.clear();
      persistResidentAuthenticatedState(response);
      navigate(response.has_completed_setup ? "/resident" : "/resident/setup", { replace: true });
    } catch (err: any) {
      setError(err?.response?.data?.message || t("resident.selectProperty.enterError"));
    } finally {
      setSubmittingId(null);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: BG }}>
      <div className="w-full max-w-2xl rounded-3xl border border-black/10 bg-white shadow-md p-6 md:p-8">
        {/* Logo */}
        <div className="flex flex-col items-center mb-6">
          <img src={logoImg} alt="Urbe360 logo" width={80} height={80} className="mb-2" />
          <span className="text-[16px] font-semibold text-gray-900">Urbe360</span>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 text-center">
            {t("resident.selectProperty.title")}
          </h1>
          <p className="mt-2 text-sm text-gray-600 text-center">
            {t("resident.selectProperty.subtitle")}
          </p>
        </div>

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 mb-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-500">
            {t("resident.selectProperty.loading")}
          </div>
        ) : memberships.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-500">
            {t("resident.selectProperty.noProperties")}
          </div>
        ) : (
          <div className="space-y-3">
            {memberships.map((membership) => (
              <button
                key={membership.resident_id}
                type="button"
                onClick={() => handleSelect(membership.resident_id)}
                disabled={submittingId === membership.resident_id}
                className="w-full rounded-2xl border border-black/10 bg-white px-5 py-4 text-left shadow-sm transition hover:border-black/20 hover:shadow-md disabled:opacity-60"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {membership.building?.name || t("resident.selectProperty.propertyFallback")}
                    </div>
                    <div className="mt-1 text-sm text-gray-600">
                      {t("resident.selectProperty.unit")} {membership.unit?.unit_code || "—"}
                      {membership.building?.code ? ` • ${membership.building.code}` : ""}
                    </div>
                    <div className="mt-2 text-xs uppercase tracking-wide text-gray-500">
                      {membership.role_display}
                      {membership.is_primary
                        ? ` • ${t("resident.selectProperty.primaryContact")}`
                        : ""}
                    </div>
                  </div>
                  <span
                    className="inline-flex items-center rounded-xl px-3 py-1.5 text-sm font-medium text-white shrink-0"
                    style={{ backgroundColor: PRIMARY }}
                  >
                    {submittingId === membership.resident_id
                      ? t("resident.selectProperty.opening")
                      : t("resident.selectProperty.enter")}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate(residentToken ? "/resident" : "/login")}
            className="text-sm text-gray-600 hover:text-gray-900 underline underline-offset-4"
          >
            {residentToken
              ? t("resident.selectProperty.backToPortal")
              : t("auth.backToLogin")}
          </button>
        </div>
      </div>
    </div>
  );
}
